import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { Offer } from '../../shared/schemas';
import { trackGoal } from '../analytics/tracker';
import { LeadForm } from './LeadForm';
import PrivacyPage from '../pages/PrivacyPage';
import ConsentPage from '../pages/ConsentPage';
import MediaConsentPage from '../pages/MediaConsentPage';
import TermsPage from '../pages/TermsPage';
import './booking.css';

type BookingContextValue = {
  /** Opens the booking dialog; `offer` preselects an excursion or a course, without it the visitor picks one. */
  openBooking: (offer?: Offer) => void;
  isBookingOpen: boolean;
  finishDoorOpening: () => void;
};
const BookingContext = createContext<BookingContextValue | null>(null);
const legalDocuments = [
  { path: '/consent', title: 'Согласие на данные', Page: ConsentPage },
  { path: '/privacy', title: 'Политика', Page: PrivacyPage },
  { path: '/media-consent', title: 'Фото и видео', Page: MediaConsentPage },
  { path: '/terms', title: 'Правила участия', Page: TermsPage },
] as const;
type LegalPath = (typeof legalDocuments)[number]['path'];

export function useBooking() {
  const value = useContext(BookingContext);
  if (!value) throw new Error('BookingProvider is required');
  return value;
}

function cssMilliseconds(value: string) {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number * (value.trim().endsWith('ms') ? 1 : 1000) : 0;
}

export function BookingProvider({ children }: { children: ReactNode }) {
  // `offer: null` — opened without a preselected offer; the whole value is null while the dialog is closed.
  const [selection, setSelection] = useState<{ offer: Offer | null } | null>(null);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const legalDialog = useRef<HTMLDialogElement>(null);
  const legalOpener = useRef<HTMLElement | null>(null);
  const bookingScroll = useRef(0);
  const [legalPath, setLegalPath] = useState<LegalPath | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const phase = useRef<'closed' | 'opening' | 'visible'>('closed');
  const openingTimer = useRef<number | null>(null);
  const location = useLocation();
  // Legal texts are mounted only in the browser: prerendered pages must not repeat them as duplicate content.
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  const clearOpeningTimer = useCallback(() => {
    if (openingTimer.current !== null) {
      window.clearTimeout(openingTimer.current);
      openingTimer.current = null;
    }
  }, []);
  const closeLegal = useCallback(() => {
    legalDialog.current?.close();
    setLegalPath(null);
    if (dialog.current) dialog.current.scrollTop = bookingScroll.current;
    if (legalOpener.current?.isConnected) legalOpener.current.focus({ preventScroll: true });
  }, []);
  const closeBooking = useCallback(() => {
    phase.current = 'closed';
    clearOpeningTimer();
    closeLegal();
    setIsDialogVisible(false);
    setSelection(null);
  }, [clearOpeningTimer, closeLegal]);
  const finishDoorOpening = useCallback(() => {
    if (phase.current !== 'opening') return;
    phase.current = 'visible';
    clearOpeningTimer();
    setIsDialogVisible(true);
  }, [clearOpeningTimer]);
  const openBooking = useCallback((offer?: Offer) => {
    if (phase.current !== 'closed') return;
    phase.current = 'opening';
    trackGoal('booking_open');
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSelection({ offer: offer ?? null });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finishDoorOpening();
  }, [finishDoorOpening]);

  useLayoutEffect(() => {
    closeBooking();
    return () => {
      phase.current = 'closed';
      clearOpeningTimer();
    };
  }, [location.key, closeBooking, clearOpeningTimer]);
  useEffect(() => {
    if (selection === null || isDialogVisible || phase.current !== 'opening') return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleMotionChange = () => {
      if (reducedMotion.matches) finishDoorOpening();
    };
    const cancelWaiting = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || phase.current !== 'opening') return;
      event.preventDefault();
      closeBooking();
    };
    reducedMotion.addEventListener('change', handleMotionChange);
    document.addEventListener('keydown', cancelWaiting);

    const leaf = document.querySelector('.brand-booking .brand-door-leaf');
    let duration = cssMilliseconds(getComputedStyle(document.documentElement).getPropertyValue('--door-opening-duration'));
    if (leaf) {
      const style = getComputedStyle(leaf);
      const properties = style.transitionProperty.split(',');
      const durations = style.transitionDuration.split(',').map(cssMilliseconds);
      const delays = style.transitionDelay.split(',').map(cssMilliseconds);
      duration = properties.reduce((longest, property, index) => {
        if (!['transform', '-webkit-transform', 'all'].includes(property.trim())) return longest;
        return Math.max(longest, durations[index % durations.length] + delays[index % delays.length]);
      }, 0);
    }
    if (reducedMotion.matches || duration <= 0) finishDoorOpening();
    else {
      // Transition events can be skipped when the header is offscreen or hidden.
      openingTimer.current = window.setTimeout(finishDoorOpening, duration * 1.1);
    }
    return () => {
      clearOpeningTimer();
      reducedMotion.removeEventListener('change', handleMotionChange);
      document.removeEventListener('keydown', cancelWaiting);
    };
  }, [selection, isDialogVisible, finishDoorOpening, closeBooking, clearOpeningTimer]);
  useEffect(() => {
    const element = dialog.current;
    if (!isDialogVisible || !element) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = opener.current;
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [isDialogVisible]);
  useLayoutEffect(() => {
    const element = legalDialog.current;
    if (legalPath === null || !element) return;
    if (!element.open) element.showModal();
    element.scrollTop = 0;
    element.querySelector<HTMLElement>('.booking-close')?.focus({ preventScroll: true });
  }, [legalPath]);

  function openLegal(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const path = new URL(event.currentTarget.href).pathname;
    if (!legalDocuments.some((document) => document.path === path)) return;
    event.preventDefault();
    if (!legalDialog.current?.open) {
      legalOpener.current = event.currentTarget;
      bookingScroll.current = dialog.current?.scrollTop ?? 0;
    }
    setLegalPath(path as LegalPath);
  }

  function handleLegalClick(event: MouseEvent<HTMLDialogElement>) {
    const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
    if (!anchor || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const path = new URL(anchor.href).pathname;
    if (legalDocuments.some((document) => document.path === path)) {
      event.preventDefault();
      event.stopPropagation();
      setLegalPath(path as LegalPath);
    } else if (path === '/') {
      event.preventDefault();
      event.stopPropagation();
      closeLegal();
    }
  }
  useEffect(() => {
    if (location.hash === '#request') {
      openBooking(location.pathname.includes('botanicheskaya') ? 'botanical_walk' : undefined);
    }
  }, [location.key, location.hash, location.pathname, openBooking]);

  function trapFocus(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== 'Tab') return;
    const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([tabindex="-1"]), select:not(:disabled), textarea:not(:disabled), a[href]')].filter((element) => element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  return <BookingContext.Provider value={{ openBooking, isBookingOpen: selection !== null, finishDoorOpening }}>
    {children}
    <dialog ref={dialog} className="booking-dialog" aria-labelledby="booking-title" onCancel={(event) => { event.preventDefault(); closeBooking(); }} onKeyDown={trapFocus} onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const box = event.currentTarget.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeBooking();
    }}>
      <div className="booking-dialog-heading"><h2 id="booking-title">Оставить заявку</h2><button type="button" className="booking-close" aria-label="Закрыть заявку" onClick={closeBooking}>×</button></div>
      {isDialogVisible && selection !== null && <LeadForm initialOffer={selection.offer} onLegalLink={openLegal} />}
    </dialog>
    <dialog ref={legalDialog} className="booking-legal-dialog" aria-labelledby="booking-legal-title" onCancel={(event) => { event.preventDefault(); closeLegal(); }} onKeyDown={trapFocus} onClickCapture={handleLegalClick} onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const box = event.currentTarget.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeLegal();
    }}>
      <div className="booking-dialog-heading"><h2 id="booking-legal-title">Документы</h2><button type="button" className="booking-close" aria-label="Закрыть документы и вернуться к заявке" onClick={closeLegal}>×</button></div>
      <nav className="booking-legal-nav" aria-label="Документы заявки">
        {legalDocuments.map(({ path, title }) => <a key={path} href={path} aria-current={legalPath === path ? 'page' : undefined}>{title}</a>)}
      </nav>
      {isClient && legalDocuments.map(({ path, Page }) => <section key={path} hidden={legalPath !== path}><Page /></section>)}
    </dialog>
  </BookingContext.Provider>;
}
